import { useCallback, useMemo, useRef, useState } from 'react'
import clsx from 'clsx'
import { useAppForm, FormField, FileInput, SelectInput } from '@tandem/ui/form'
import $type from '@tandem/ui/type.css'
import $form from '@tandem/ui/form.css'
import $table from '@tandem/ui/table.css'
import $layout from '@tandem/ui/layout.css'
import $ from './-ImportPage.css'
import { useTRPC } from '@/trpc'
import { useMutation, useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import Spinner from '@tandem/ui/Spinner'
import { useVirtualizer } from '@tanstack/react-virtual'
import { ALLOWED_EXTENSIONS, getExtension, parseSpreadsheet, parseYmlCatalog, type Row } from '@/catalog/import'
import { FunnelXIcon } from 'lucide-react'

let IMAGE_RE = /\.(?:jpe?g|png|gif|webp|avif|svg|bmp|tiff?)(?:[?#]|$)/i
let URL_RE = /^https?:\/\/\S+$/i

function CellValue({ value }: { value: string }) {
  if (IMAGE_RE.test(value)) {
    let urls = value.split(/,\s*/).filter(u => IMAGE_RE.test(u))
    if (urls.length > 0) {
      return (
        <span class={$.thumbCell}>
          {urls.map((url, i) => (
            <a
              key={i}
              href={url}
              target='_blank'
              rel='noreferrer noopener'
              onClick={(e) => e.stopPropagation()}
            >
              <img src={url} class={$.thumb} loading='lazy' alt='' />
            </a>
          ))}
        </span>
      )
    }
  }
  let trimmed = value.trim()
  if (URL_RE.test(trimmed)) {
    return (
      <a
        href={trimmed}
        target='_blank'
        rel='noreferrer noopener'
        class={$type.underline}
        onClick={(e) => e.stopPropagation()}
      >
        {value}
      </a>
    )
  }
  return <>{value}</>
}

export const Route = createFileRoute('/catalog/import/add')({
  component: ImportUploadPage,
})

function ImportUploadPage() {
  let [headers, setHeaders] = useState<string[]>([])
  let [rows, setRows] = useState<Row[]>([])
  let [error, setError] = useState<string | null>(null)
  let [selected, setSelected] = useState<Set<number>>(new Set())

  let trpc = useTRPC()
  let navigate = useNavigate()

  let { data: brands } = useQuery(trpc.brand.list.queryOptions())
  let createImport = useMutation(trpc.import.upload.mutationOptions())

  let form = useAppForm({
    defaultValues: {
      file: null as File | null,
      brand: null as string | null
    },
    canSubmitWhenInvalid: true,
    validators: {
      async onSubmitAsync() {
        if (headers.length === 0 || rows.length === 0) return
        try {
          let brandId = form.getFieldValue('brand')
          let file = form.getFieldValue('file')
          let submitRows = selected.size > 0 ? rows.filter((_, i) => selected.has(i)) : rows
          let { id } = await createImport.mutateAsync({
            header: headers,
            rows: submitRows,
            brand_id: brandId ? Number(brandId) : null,
            original_filename: file?.name ?? null
          })
          navigate({ to: '/catalog/import/$id', params: { id: String(id) } })
        } catch (e) {
          console.error(e)
        }
      }
    }
  })

  async function handleFileChange(file: File | File[] | null) {
    if (Array.isArray(file)) return
    setError(null)
    setHeaders([])
    setRows([])
    setSelected(new Set())

    if (!file) return

    let ext = getExtension(file.name)
    if (!ALLOWED_EXTENSIONS.includes(ext)) {
      setError('Неподдерживаемый формат. Допустимые: .csv, .xls, .xlsx')
      return
    }

    try {
      if (ext.startsWith('.xls')) {
        let result = parseSpreadsheet(await file.arrayBuffer())
        if (!result) {
          setError('Файл пуст')
          return
        }
        setHeaders(result.headers)
        setRows(result.rows)
      } else if (ext === '.xml') {
        let text = await file.text()
        let result = parseYmlCatalog(text)
        if (!result) {
          setError('Неподдерживаемый формат XML. Поддерживается формат Яндекс Маркет.')
          return
        }
        setHeaders(result.headers)
        setRows(result.rows)
      }
    } catch (e) {
      console.error(e)
      setError('Не удалось прочитать файл.')
    }
  }

  let withData = useMemo(() => headers.length > 0, [headers])

  let [filters, setFilters] = useState({})
  let toggleFilter = useCallback((header, val) => {
    setFilters(filters => ({
      ...filters,
      [header]: filters[header] === val ? undefined : val
    }))
  }, [rows])

  let [pinnedOrder, setPinnedOrder] = useState<number[] | null>(null)

  let visibleRows = useMemo(() => {
    let filtered = rows
      .map((row, i) => ({ row, i }))
      .filter(({ row }) => !Object.keys(filters).find(header => row[header] !== filters[header] && filters[header] !== undefined))
    if (pinnedOrder) {
      let orderMap = new Map(pinnedOrder.map((idx, pos) => [idx, pos]))
      filtered.sort((a, b) => (orderMap.get(a.i) ?? a.i) - (orderMap.get(b.i) ?? b.i))
    }
    return filtered
  }, [rows, filters, pinnedOrder])
  
  let scrollerRef = useRef<HTMLDivElement>(null)

  let rowVirtualizer = useVirtualizer({
    count: visibleRows.length,
    getScrollElement: () => scrollerRef.current,
    estimateSize: () => 33,
    overscan: 20,
  })

  return (
    <div class={$.page}>
      <div class={clsx($type.breadcrumb, $type.small)}>
        <a href='/catalog' class={$type.underline}>
          Каталог
        </a>
        <span>/</span>
        <a href='/catalog/import' class={$type.underline}>
          Импорт
        </a>
        <span>/</span>
        <b>Добавить выгрузку</b>
      </div>

      <div class={clsx($layout.container, $layout.center)}>

        <div class={clsx($layout.collapsible, withData && $layout.collapsed)}>
          <div>
            <h3 class={$.box}>Добавить выгрузку</h3>
          </div>
        </div>

        <form
          onSubmit={(e) => {
            e.preventDefault()
            e.stopPropagation()
            form.handleSubmit()
          }}
        >
          <div class={clsx($.importForm)}>
            <form.Field name='file'>
              {(field) => (
                <FormField field={field} footnote='CSV, XLS или XLSX'>
                  <FileInput
                    field={field}
                    label='Файл выгрузки'
                    accept='.csv,.xls,.xlsx,.xml'
                    onValueChange={handleFileChange}
                  />
                </FormField>
              )}
            </form.Field>
            <form.Field name='brand'>
              {(field) => (
                <FormField field={field} label='Бренд' footnote='Оставьте пустым для мультрибрендовой выгрузки.'>
                  <SelectInput
                    field={field}
                    options={(brands || []).map((brand) => ({
                      value: brand.id,
                      label: brand.name
                    }))}
                  />
                </FormField>
              )}
            </form.Field>
            <form.Subscribe selector={(state) => [state.isSubmitting]}>
              {([isSubmitting]) => (
                <div class={$form.floatAction}>
                  <button class='secondary' type='submit' disabled={rows.length === 0 || isSubmitting}>
                    {isSubmitting && <Spinner />}
                    {selected.size > 0 ? `Загрузить (${selected.size})` : 'Загрузить'}
                  </button>
                </div>
              )}
            </form.Subscribe>
          </div>

          {error && <p style={{ color: '#dc2626', marginTop: '1em' }}>{error}</p>}
        </form>

        {headers.length > 0 && (
          <p class={$type.small} style={{ marginTop: 0, color: '#666' }}>
            Всего: {rows.length}.{visibleRows.length < rows.length && ` Показано: ${visibleRows.length}.`}{selected.size > 0 && <> <button class={clsx('plain', $type.underline)} onClick={() => {
              let selectedFirst = visibleRows.filter(({ i }) => selected.has(i)).map(({ i }) => i)
              let rest = visibleRows.filter(({ i }) => !selected.has(i)).map(({ i }) => i)
              setPinnedOrder([...selectedFirst, ...rest])
              scrollerRef.current?.scrollTo({ top: 0, left: 0, behavior: 'smooth' })
            }}>Выбрано: {selected.size}.</button></>}
          </p>
        )}
      </div>

      {headers.length > 0 && (
        <div class={$.tableScroller} ref={scrollerRef}>
          <table class={clsx($.table, $table.table, $table.condensed, $type.small)}>
            <thead>
              <tr>
                <th>
                  <input
                    type='checkbox'
                    checked={visibleRows.length > 0 && visibleRows.every(({ i }) => selected.has(i))}
                    ref={(el) => {
                      if (el) {
                        let visibleSelected = visibleRows.filter(({ i }) => selected.has(i)).length
                        el.indeterminate = visibleSelected > 0 && visibleSelected < visibleRows.length
                      }
                    }}
                    onChange={() => {
                      let allSelected = visibleRows.every(({ i }) => selected.has(i))
                      if (allSelected) {
                        setSelected(prev => {
                          let next = new Set(prev)
                          visibleRows.forEach(({ i }) => next.delete(i))
                          return next
                        })
                      } else {
                        setSelected(prev => {
                          let next = new Set(prev)
                          visibleRows.forEach(({ i }) => next.add(i))
                          return next
                        })
                      }
                    }}
                  />
                </th>
                {headers.map((h, i) => (
                  <th key={i}>
                    <div class={$layout.row}>
                      {h}
                      {filters[h] && (
                        <button class='plain' onClick={() => setFilters(filters => ({ ...filters, [h]: undefined }))}>
                          <FunnelXIcon />
                        </button>
                      )}
                    </div>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowVirtualizer.getVirtualItems().length > 0 && (
                <tr style={{ height: rowVirtualizer.getVirtualItems()[0].start }} aria-hidden />
              )}
              {rowVirtualizer.getVirtualItems().map((virtualRow) => {
                let { row, i } = visibleRows[virtualRow.index]
                return (
                  <tr
                    key={i}
                    style={{
                      height: `${virtualRow.size}px`,
                      opacity: selected.size > 0 && !selected.has(i) ? 0.5 : 1,
                    }}
                  >
                    <td>
                      <input
                        type='checkbox'
                        checked={selected.has(i)}
                        onChange={() => {
                          setSelected((prev) => {
                            let next = new Set(prev)
                            if (next.has(i)) next.delete(i)
                            else next.add(i)
                            return next
                          })
                        }}
                      />
                    </td>
                    {headers.map((header) => (
                      <td
                        key={header}
                        onClick={() => toggleFilter(header, row[header])}
                        title={String(row[header] ?? '')}
                        class={clsx({ [$.filtered]: !!filters[header] })}
                      ><CellValue value={String(row[header] ?? '')} /></td>
                    ))}
                  </tr>
                )
              })}
              {rowVirtualizer.getVirtualItems().length > 0 && (() => {
                let items = rowVirtualizer.getVirtualItems()
                let last = items[items.length - 1]!
                return <tr style={{ height: rowVirtualizer.getTotalSize() - (last.start + last.size) }} aria-hidden />
              })()}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}
